import React from 'react';
import { Button, Row, Col, Tooltip } from 'antd';
import { EyeOutlined, HistoryOutlined } from '@ant-design/icons';
import {
  CustomModal,
  CustomTable,
  GeneralContainer,
} from '../../../../components';
import SearchRow from '../components/Filters';
import { ApproveRequestsResponse } from '../../types/approveRequests-response';
import { PetitionsResponse } from '../../../Petitions/types';
import CancelRequest from '../components/ByRoles/CancelRequest';
import useApproveRequestsFormik from '../../../../utils/validators/approve-request/approve-request-validator';
import CustomAlert from '../../../../components/CustomAlert/CustomAlert';

interface ApproveRequestViewProps {
  data?: ApproveRequestsResponse | PetitionsResponse;
  // onViewCreate: () => void;
  onPagination: (page: number, pageSize: number) => void;
  onViewDetail: (record: any) => void;
  formik: any;
  isLoading: boolean;
  onResetFilters: () => void;
  onCancelRequest: (id: string, reason: string) => Promise<void>;
}

const ApproveRequestView: React.FC<ApproveRequestViewProps> = ({
  data,
  // onViewCreate,
  onViewDetail,
  onPagination,
  formik,
  isLoading,
  onResetFilters,
  onCancelRequest,
}) => {
  const [modalCancel, setModalCancel] = React.useState(false);
  const [requestId, setRequestId] = React.useState('');

  const onConfirmModal = () => {
    onCancelRequest(requestId, formikCancelRequest.values.reason).finally(
      () => {
        setModalCancel(false);
        formikCancelRequest.resetForm();
      }
    );
  };

  const columns = [
    {
      title: 'Número ticket',
      dataIndex: 'ticket',
      key: 'ticket',
      sorter: (a: any, b: any) => a.ticket.localeCompare(b.ticket),
      align: 'center',
    },
    {
      title: 'Tipo ticket',
      dataIndex: 'ticket_type',
      key: 'ticket_type',
      sorter: (a: any, b: any) => a.ticket_type.localeCompare(b.ticket_type),
      align: 'center',
      responsive: ['lg'],
    },
    {
      title: 'Fecha de solicitud',
      dataIndex: 'query_date',
      key: 'query_date',
      sorter: (a: any, b: any) => a.query_date.localeCompare(b.query_date),
      align: 'center',
    },
    {
      title: 'Regional',
      dataIndex: 'regional',
      key: 'regional',
      sorter: (a: any, b: any) => a.regional.localeCompare(b.regional),
      align: 'center',
      responsive: ['lg'],
    },
    {
      title: 'Seccional',
      dataIndex: 'sectional',
      key: 'sectional',
      sorter: (a: any, b: any) => a.sectional.localeCompare(b.sectional),
      align: 'center',
      responsive: ['lg'],
    },
    {
      title: 'Estado solicitud',
      dataIndex: 'status_id',
      key: 'status_id',
      sorter: (a: any, b: any) => a.status.localeCompare(b.status),
      align: 'center',
      render: (status: string) => {
        return <CustomAlert status={status} />
      },
    },
    {
      title: 'Solicitante',
      dataIndex: 'requester',
      key: 'requester',
      sorter: (a: any, b: any) => a.requester.localeCompare(b.requester),
      align: 'center',
      render: (_: any, record: any) => `${record.requester_doc} - ${record.requester}`,
    },
    {
      title: 'Observaciones',
      dataIndex: 'observations',
      key: 'observations',
      align: 'center',
      width: 300,
      render: (text: string) => {
        return (
          <Tooltip title={text || 'Sin registro'}>
            <div
              style={{
                maxWidth: 250,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                margin: '0 auto',
              }}
            >
              {text || 'Sin registro'}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      render: (_: any, record: any) => (
        <>
          {record.can_manage ? 
            <Tooltip title="Gestionar solicitud">
              <Button
                type="link"
                icon={<HistoryOutlined />}
                onClick={() => onViewDetail(record)}
              />
            </Tooltip> :
            <Tooltip title="Ver solicitud">
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => onViewDetail(record)}
              />
            </Tooltip>
          }
        </>
      ),
    },
  ];

  const formikCancelRequest = useApproveRequestsFormik({ onSubmit: () => {} });

  return (
    <GeneralContainer title="Solicitudes por aprobar" isLoading={isLoading}>
      {/* Filtros */}
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col xs={24}>
          <SearchRow
            formik={formik}
            onCancel={onResetFilters}
            isLoading={isLoading}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={24}>
          <CustomTable
            loading={isLoading}
            columns={columns as any}
            dataSource={data?.data?.map((refund) => ({
              ...refund,
              id: Number(refund.id) || -1,
            }))}
            paginationConfig={{
              current: data?.current_page,
              pageSize: data?.per_page,
              total: data?.total,
              onChange: onPagination,
            }}
            scroll={{ x: 'max-content' }}
          />
        </Col>
      </Row>

      <CustomModal
        title=""
        labelSubmit="Aceptar"
        open={modalCancel}
        width={1000}
        onCancel={() => setModalCancel(false)}
        onSave={onConfirmModal}
        disabledBtn={!formikCancelRequest.values.reason}
      >
        <CancelRequest formik={formikCancelRequest} />
      </CustomModal>
    </GeneralContainer>
  );
};

export default ApproveRequestView;
